import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, User, ArrowRight, Lock, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useGlobale from '../components/global';
import api from '../components/api';
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const currentYear = new Date().getFullYear();
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const navigate = useNavigate();
  const { login, authenticated_collecte } = useGlobale();

  useEffect(() => {
    loadCaptchaEnginge(6);
    // Vérification de l'authentification et redirection
    if (authenticated_collecte) {
      navigate('/dashboard');
    }
  }, [authenticated_collecte, navigate]);
  
  const refreshCaptcha = () => {
    loadCaptchaEnginge(6);
    setCaptchaValue('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!captchaValue) {
      setError('Veuillez compléter le captcha');
      return;
    }

    if (!validateCaptcha(captchaValue)) {
      setError('Captcha incorrect');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await api.post('/utilisateur/login/', {
        username: email,
        password: password
      });

      if (response.status === 200) {
        const userData = response.data;
        const authData = {
          access: userData.tokens.access,
          refresh: userData.tokens.refresh
        };
        localStorage.setItem('auth_tokens', JSON.stringify(authData));

        await login(authData, userData.user);
        navigate('/dashboard');
      }
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setError('Veuillez remplir tous les champs correctement');
            break;
          case 401:
            setError('Identifiants incorrects');
            break;
          default:
            setError('Une erreur est survenue lors de la connexion');
        }
      } else if (error.request) {
        console.log('Request:', {
          method: error.request.method,
          url: error.request.url,
          status: error.request.status,
          statusText: error.request.statusText,
          responseText: error.request.responseText
        });
        setError('Le serveur ne répond pas, veuillez réessayer plus tard');
      } else {
        setError('Impossible d\'établir la connexion au serveur');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 bg-green-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 bg-emerald-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-1/3 h-1/3 bg-teal-400/20 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/90 backdrop-blur-xl rounded-xl shadow-xl overflow-hidden border border-white/20">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 flex justify-center items-center space-x-4">
            <img src="/anrp.png" alt="ANRP" className="h-10 rounded-lg bg-white/95 p-1 shadow-md" />
            <img src="/lafiaya.jpg" alt="Lahiya Iyali" className="h-10 rounded-lg bg-white/95 p-1 shadow-md" />
          </div>

          <div className="p-5">
            <div className="text-center mb-4">
              <h1 className="text-xl font-bold text-gray-800">Connexion</h1>
              <p className="text-sm text-gray-600">Accédez à votre espace de travail</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-2 rounded-md text-xs">
                  <p className="font-medium flex items-center"><span className="mr-2">⚠️</span>{error}</p>
                </div>
              )}
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <User className="h-4 w-4 mr-1 text-emerald-600" />
                  Email <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative group">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/80 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:border-green-500 transition-all pl-9"
                    placeholder="vous@exemple.com"
                    required
                  />
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <Lock className="h-4 w-4 mr-1 text-emerald-600" />
                  Mot de passe <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white/80 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:border-emerald-500 transition-all pl-9"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700 flex items-center">
                    Captcha <span className="text-red-500 ml-1">*</span>
                  </label>
                  <button 
                    type="button" 
                    onClick={refreshCaptcha} 
                    className="text-xs text-emerald-600 flex items-center"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Actualiser
                  </button>
                </div>
                <div className="flex flex-col items-center space-y-2 bg-gray-50 p-2 rounded-lg border border-gray-100">
                  <div className="rounded">
                    <LoadCanvasTemplate />
                  </div>
                  <input
                    type="text"
                    value={captchaValue}
                    onChange={(e) => setCaptchaValue(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-center font-mono"
                    placeholder="Entrez le captcha"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-between py-1">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-gray-300 text-green-600"
                  />
                  <span className="ml-2 text-xs text-gray-600">Se souvenir de moi</span>
                </label>
                <a href="#" className="text-xs text-emerald-600 font-medium">
                  Mot de passe oublié?
                </a>
              </div>

              <div className="space-y-2">
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:opacity-90 text-white py-2.5 rounded-lg font-medium shadow-lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center text-sm">
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Chargement...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center text-sm">
                      Se connecter
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full bg-white border border-gray-300 text-gray-700 py-2 rounded-lg font-medium text-sm"
                >
                  Revenir sur le site
                </button>
              </div>
            </form>

            <div className="mt-4 pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Développée par Aydees • © {currentYear} ANRP - Lafia Iyali
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;